import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Wallet, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { formatDateBR, todayLocalISO } from '../utils/formatDate';
import AnimalSearchSelect from '../components/AnimalSearchSelect';

const hoje = todayLocalISO();

export default function Vendas() {
  const [vendas, setVendas] = useState([]);
  const [indicadores, setIndicadores] = useState(null);
  const [animais, setAnimais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [animalSelecionado, setAnimalSelecionado] = useState('');
  const [animalSelecionadoObj, setAnimalSelecionadoObj] = useState(null);
  const [form, setForm] = useState({
    data_venda: hoje,
    tipo_venda: 'corte',
    comprador: '',
    observacao: '',
    animais: [],
  });

  const carregar = async () => {
    setLoading(true);
    try {
      const [vRes, indRes, aRes] = await Promise.all([
        api.get('/vendas'),
        api.get('/vendas/indicadores'),
        api.get('/animais', { params: { situacao: 'cabana', limit: 1000 } }),
      ]);
      setVendas(vRes.data || []);
      setIndicadores(indRes.data || null);
      setAnimais(aRes.data.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const totaisForm = useMemo(() => {
    const valor = form.animais.reduce((s, a) => s + (parseFloat(a.valor_venda) || 0), 0);
    const peso = form.animais.reduce((s, a) => s + (parseFloat(a.peso_kg) || 0), 0);
    return { valor, peso };
  }, [form.animais]);

  const adicionarAnimal = () => {
    if (!animalSelecionado) return;
    if (form.animais.some(a => String(a.animal_id) === String(animalSelecionado))) return toast.error('Animal já incluído');
    const animal = animalSelecionadoObj || animais.find(a => String(a.id) === String(animalSelecionado));
    if (!animal) return toast.error('Selecione um animal válido');
    setForm(p => ({
      ...p,
      animais: [...p.animais, { animal_id: animal.id, brinco: animal.brinco, nome: animal.nome, categoria: animal.categoria, peso_kg: '', valor_venda: '' }],
    }));
    setAnimalSelecionado('');
    setAnimalSelecionadoObj(null);
  };

  const atualizarAnimal = (idx, campo, valor) => {
    setForm(p => ({
      ...p,
      animais: p.animais.map((a, i) => i === idx ? { ...a, [campo]: valor } : a),
    }));
  };

  const removerAnimal = (idx) => {
    setForm(p => ({ ...p, animais: p.animais.filter((_, i) => i !== idx) }));
  };

  const salvar = async () => {
    if (!form.animais.length) return toast.error('Inclua ao menos um animal');
    if (form.animais.some(a => !parseFloat(a.valor_venda))) return toast.error('Informe o valor de venda de todos os animais');
    try {
      await api.post('/vendas', form);
      toast.success('Venda registrada e rentabilidade calculada');
      setModal(false);
      setForm({ data_venda: hoje, tipo_venda: 'corte', comprador: '', observacao: '', animais: [] });
      carregar();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao registrar venda');
    }
  };

  const fmtBRL = (v) => `R$ ${parseFloat(v || 0).toFixed(2)}`;

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h1>Vendas & Rentabilidade</h1>
          <p>Apuração de venda, custo acumulado, lucro e margem</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal(true)}><Plus size={16} /> Nova Venda</button>
      </div>

      <div className="stats-grid" style={{ marginBottom:16 }}>
        <div className="stat-card">
          <div className="stat-label">Receita de Vendas</div>
          <div className="stat-value" style={{ color:'#166534' }}>{fmtBRL(vendas.reduce((s, v) => s + parseFloat(v.valor_total || 0), 0))}</div>
          <div className="stat-sub">{vendas.length} negociação(ões)</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Lucro Apurado</div>
          <div className="stat-value" style={{ color:'#1D4ED8' }}>{fmtBRL(vendas.reduce((s, v) => s + parseFloat(v.lucro_total || 0), 0))}</div>
          <div className="stat-sub">receita menos custos individuais</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Animais Vendidos</div>
          <div className="stat-value" style={{ color:'#7C3AED' }}>{vendas.reduce((s, v) => s + parseInt(v.total_animais || 0), 0)}</div>
          <div className="stat-sub">{parseFloat(vendas.reduce((s, v) => s + parseFloat(v.peso_total_kg || 0), 0)).toFixed(1)} kg informados</div>
        </div>
      </div>

      {indicadores && (
        <>
          <div className="stats-grid" style={{ marginBottom:16 }}>
            <div className="stat-card">
              <div className="stat-label">Custo do Rebanho</div>
              <div className="stat-value" style={{ color:'#334155' }}>{fmtBRL(indicadores.rebanho?.custo_total)}</div>
              <div className="stat-sub">{fmtBRL(indicadores.rebanho?.custo_medio_animal)} por animal na cabaña</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Custo por kg Vendido</div>
              <div className="stat-value" style={{ color:'#B45309' }}>{fmtBRL(indicadores.vendas?.custo_por_kg_vendido)}</div>
              <div className="stat-sub">receita/kg: {fmtBRL(indicadores.vendas?.receita_por_kg_vendido)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Cordeiro Desmamado</div>
              <div className="stat-value" style={{ color:'#0E7490' }}>{fmtBRL(indicadores.cordeiros_desmamados?.custo_por_cordeiro)}</div>
              <div className="stat-sub">{indicadores.cordeiros_desmamados?.total || 0} cordeiros desmamados</div>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              <div style={{ padding:'14px 16px', fontWeight:700 }}>Custos por Categoria</div>
              <table style={{ fontSize:13 }}>
                <thead><tr><th>Categoria</th><th>Animais</th><th style={{ textAlign:'right' }}>Custo</th><th style={{ textAlign:'right' }}>Média</th></tr></thead>
                <tbody>{(indicadores.por_categoria || []).map(c => (
                  <tr key={c.categoria}>
                    <td style={{ textTransform:'capitalize' }}>{c.categoria.replace('_', ' ')}</td>
                    <td>{c.animais}</td>
                    <td style={{ textAlign:'right' }}>{fmtBRL(c.custo_total)}</td>
                    <td style={{ textAlign:'right' }}>{fmtBRL(c.animais > 0 ? c.custo_total / c.animais : 0)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div className="card" style={{ padding:0, overflow:'hidden' }}>
              <div style={{ padding:'14px 16px', fontWeight:700 }}>Custos por Lote</div>
              <table style={{ fontSize:13 }}>
                <thead><tr><th>Lote</th><th>Animais</th><th style={{ textAlign:'right' }}>Custo</th><th style={{ textAlign:'right' }}>Média</th></tr></thead>
                <tbody>{(indicadores.por_lote || []).map(l => (
                  <tr key={l.lote}>
                    <td>{l.lote}</td>
                    <td>{l.animais}</td>
                    <td style={{ textAlign:'right' }}>{fmtBRL(l.custo_total)}</td>
                    <td style={{ textAlign:'right' }}>{fmtBRL(l.animais > 0 ? l.custo_total / l.animais : 0)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        <table>
          <thead><tr><th>Data</th><th>Tipo</th><th>Comprador</th><th>Animais</th><th>Peso</th><th>Receita</th><th>Custo</th><th>Lucro</th><th>Margem</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={9} style={{ textAlign:'center', padding:40 }}>Carregando...</td></tr>
            : vendas.length === 0 ? <tr><td colSpan={9}><div className="empty-state"><Wallet size={42} /><p>Nenhuma venda registrada</p></div></td></tr>
            : vendas.map(v => (
              <tr key={v.id}>
                <td>{formatDateBR(v.data_venda)}</td>
                <td style={{ textTransform:'capitalize' }}>{v.tipo_venda}</td>
                <td>{v.comprador || '—'}</td>
                <td>{v.total_animais}</td>
                <td>{v.peso_total_kg ? `${parseFloat(v.peso_total_kg).toFixed(1)} kg` : '—'}</td>
                <td style={{ fontWeight:600 }}>{fmtBRL(v.valor_total)}</td>
                <td>{fmtBRL(v.custo_total)}</td>
                <td style={{ fontWeight:700, color: parseFloat(v.lucro_total) >= 0 ? '#166534' : '#B91C1C' }}>{fmtBRL(v.lucro_total)}</td>
                <td>{parseFloat(v.margem_percentual || 0).toFixed(2)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal" style={{ maxWidth:800, maxHeight:'90vh', overflow:'auto' }}>
            <div className="modal-header">
              <h2>Nova Venda</h2>
              <button className="btn btn-secondary btn-sm" onClick={() => setModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group"><label>Data *</label><input type="date" value={form.data_venda} onChange={e => setForm(p => ({ ...p, data_venda:e.target.value }))} /></div>
                <div className="form-group"><label>Tipo *</label>
                  <select value={form.tipo_venda} onChange={e => setForm(p => ({ ...p, tipo_venda:e.target.value }))}>
                    <option value="corte">Corte</option>
                    <option value="matriz">Matriz</option>
                    <option value="reprodutor">Reprodutor</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
              </div>
              <div className="form-group"><label>Comprador</label><input value={form.comprador} onChange={e => setForm(p => ({ ...p, comprador:e.target.value }))} /></div>

              <div style={{ display:'flex', gap:8, alignItems:'end', marginBottom:12 }}>
                <div className="form-group" style={{ flex:1, marginBottom:0 }}><label>Animal</label>
                  <AnimalSearchSelect
                    value={animalSelecionado}
                    onChange={(id, animal) => { setAnimalSelecionado(id); setAnimalSelecionadoObj(animal || null); }}
                    situacao="cabana"
                    emptyLabel="Selecione animal na cabana"
                  />
                </div>
                <button className="btn btn-secondary" onClick={adicionarAnimal}><Plus size={16} /> Incluir</button>
              </div>

              {form.animais.length > 0 && (
                <table style={{ fontSize:13, marginBottom:12 }}>
                  <thead><tr><th>Animal</th><th>Categoria</th><th>Peso kg</th><th>Valor venda</th><th></th></tr></thead>
                  <tbody>{form.animais.map((a, idx) => (
                    <tr key={a.animal_id}>
                      <td><strong>{a.brinco}</strong>{a.nome ? ` — ${a.nome}` : ''}</td>
                      <td style={{ textTransform:'capitalize' }}>{a.categoria}</td>
                      <td><input type="number" step="0.1" min="0" value={a.peso_kg} disabled={form.tipo_venda !== 'corte'} onChange={e => atualizarAnimal(idx, 'peso_kg', e.target.value)} /></td>
                      <td><input type="number" step="0.01" min="0.01" value={a.valor_venda} onChange={e => atualizarAnimal(idx, 'valor_venda', e.target.value)} /></td>
                      <td><button className="btn btn-secondary btn-sm" style={{ color:'#B91C1C' }} onClick={() => removerAnimal(idx)}><Trash2 size={13} /></button></td>
                    </tr>
                  ))}</tbody>
                </table>
              )}

              <div style={{ padding:12, background:'#F8FAFC', border:'1px solid var(--border-color)', borderRadius:8, marginBottom:12 }}>
                Total da venda: <strong>{fmtBRL(totaisForm.valor)}</strong>
                {form.tipo_venda === 'corte' ? <span> · Peso: <strong>{totaisForm.peso.toFixed(1)} kg</strong></span> : null}
              </div>
              <div className="form-group"><label>Observações</label><textarea rows={3} value={form.observacao} onChange={e => setForm(p => ({ ...p, observacao:e.target.value }))} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvar}>Registrar Venda</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
